import Axios from "axios";
import Router from "vue-router";
import Store from "@/store/store";
import { MutationNames } from "@/store/mutations";
import { AuthMode, LoginResult, User } from "./types";
import store from "@/store/store";

// what random value is being cached
enum RandomStoreType {
  State,
  Nonce
}

let silentRenewTimeoutHandlerId: number | null = null;

export default class Auth {
  // used by routing, if the route requires autentication
  public static RouterAuthCheck(to: any, from: any, next: any) {
    if (!Store.state.User.AccessToken) {
      // not loaded, redirect arround to login. store redirect info first.
      Auth.storeCallbackRedirectTo(to.fullPath);
      location.href = Auth.generateLoginUrl(AuthMode.Callback);
    } else {
      // loaded, continute
      next();
    }
  }

  public static GetCallbackRedirectTo(): string {
    return localStorage.getItem(Auth.getCallbackRedirectToKeyName()) || "/";
  }

  public static RedirectToLogin() {
    Auth.storeCallbackRedirectTo("/");
    location.href = Auth.generateLoginUrl(AuthMode.Callback);
  }

  public static Logout() {
    let url = Store.state.Environment.IdentityServerBaseUrl + "/logout";

    location.href = url;
  }

  public static async LoginUsingHashFragment(
    mode: AuthMode,
    hashArg: string
  ): Promise<[LoginResult, string]> {
    let hash = hashArg || "";
    if (hash.startsWith("#")) {
      hash = hash.substr(1);
    }
    const splitHash = hash.split("&");

    let token: string | null = null;
    let expiresIn: number | null = null;
    let state: string | null = null;

    for (const item of splitHash) {
      const kvp = item.split("=");
      if (kvp.length === 2) {
        switch (kvp[0].toLowerCase()) {
          case "access_token":
            token = kvp[1];
            break;
          case "expires_in":
            expiresIn = parseInt(kvp[1], 10);
            break;
          case "state":
            state = kvp[1];
            break;
          case "error":
            return [
              LoginResult.Error,
              "login error: " + decodeURIComponent(kvp[1])
            ];
        }
      }
    }
    if (token == null) {
      return [LoginResult.Error, "invalid response, missing access_token"];
    }

    if (expiresIn == null) {
      return [LoginResult.Error, "invalid response, missing expires_in"];
    }

    if (state == null) {
      return [LoginResult.Error, "invalid response, missing state"];
    }

    if (state !== Auth.getRandomFromStore(mode, RandomStoreType.State)) {
      return [LoginResult.Error, "invalid response, state missmatch"];
    }

    Auth.ClearRefreshTimeout();
    if (expiresIn > 360) {
      // 5 minutes sku to allow time to refresh
      expiresIn = expiresIn - 300;
      silentRenewTimeoutHandlerId = window.setTimeout(() => {
        const url = Auth.generateLoginUrl(AuthMode.SilentRenew);
        let elem = document.getElementById("identitySilentRenewIFrame");
        if (elem === null) {
          elem = document.createElement("iframe");
          elem.style.display = "none";
          elem.style.visibility = "hidden";
          elem.style.position = "absolute";
          elem.style.width = "0";
          elem.style.height = "0";
          document.body.appendChild(elem);
        }
        const frame = elem as HTMLIFrameElement;
        if (frame === null) {
          return;
        }
        frame.src = url;
        window.setTimeout(() => {
          // wait for redirect, attempt login
          try {
            if (frame !== null) {
              const updatedUrl = frame!.contentWindow!.location!.hash;
              Auth.LoginUsingHashFragment(AuthMode.SilentRenew, updatedUrl);
            }
          } catch (ex) {
            // ignore error
          }
        }, 40 * 1000);
      }, expiresIn * 1000);
    }

    const res = await Axios.get(Store.state.Environment.AuthUrl, {
      headers: { Authorization: "Bearer " + token }
    });

    switch (res.status) {
      case 200:
        const usr = res.data as User;
        if (usr === null) {
          Auth.ClearRefreshTimeout();
          return [
            LoginResult.Error,
            "invalid response, body missing required values"
          ];
        }
        usr.AccessToken = token;
        usr.UserCode = usr.UserCode || "_SU";

        Store.commit(MutationNames.SetUser, usr);
        return [LoginResult.Success, ""];
      case 401:
        Auth.ClearRefreshTimeout();
        return [LoginResult.Expired, ""];
      case 403:
        Auth.ClearRefreshTimeout();
        return [LoginResult.Forbidden, ""];
      case 500:
      default:
        Auth.ClearRefreshTimeout();
        return [
          LoginResult.Error,
          "invalid response, unexpected result: " +
            res.status.toString() +
            " - " +
            res.statusText
        ];
    }
  }

  private static generateLoginUrl(mode: AuthMode): string {
    const redirectUrl =
      location.protocol + "//" + location.host + "/identity/callback";
    let url =
      Store.state.Environment.IdentityServerBaseUrl +
      "/authorize" +
      "?client_id=" +
      Store.state.Environment.ClientId +
      "&scope=" +
      encodeURIComponent(
        `profile ${Store.state.Environment.ClaimPrefix}/prospect/Prospectsoft.OData`
      ) +
      "&redirect_uri=" +
      encodeURIComponent(redirectUrl) +
      "&response_type=" +
      encodeURIComponent("token") +
      "&state=" +
      encodeURIComponent(
        Auth.generateRandomAndStoreValue(mode, RandomStoreType.State)
      ) +
      "&nonce=" +
      encodeURIComponent(
        Auth.generateRandomAndStoreValue(mode, RandomStoreType.Nonce)
      );

    if (mode === AuthMode.SilentRenew) {
      url += "&prompt=none";
    }
    return url;
  }

  private static ClearRefreshTimeout() {
    if (silentRenewTimeoutHandlerId !== null) {
      window.clearTimeout(silentRenewTimeoutHandlerId);
      silentRenewTimeoutHandlerId = null;
    }
  }

  private static getRandomKeyName(mode: AuthMode, storeType: RandomStoreType) {
    return "randomStore_" + mode.toString() + "_" + storeType.toString();
  }

  private static getCallbackRedirectToKeyName(): string {
    return "callbackRedirectTo";
  }

  private static storeCallbackRedirectTo(path: string) {
    localStorage.setItem(Auth.getCallbackRedirectToKeyName(), path);
  }

  private static getRandomFromStore(
    mode: AuthMode,
    storeType: RandomStoreType
  ): string | null {
    return localStorage.getItem(Auth.getRandomKeyName(mode, storeType));
  }

  private static generateRandomAndStoreValue(
    mode: AuthMode,
    storeType: RandomStoreType
  ): string {
    const val = Auth.generateRandom();
    localStorage.setItem(Auth.getRandomKeyName(mode, storeType), val);
    return val;
  }

  private static generateRandom() {
    const guidHolder = "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx";
    const hex = "0123456789abcdef";
    let r = 0;
    let guidResponse = "";
    /* tslint:disable-next-line:prefer-for-of */
    for (let i = 0; i < guidHolder.length; i++) {
      if (guidHolder[i] !== "-" && guidHolder[i] !== "4") {
        // each x and y needs to be random
        /* tslint:disable-next-line:no-bitwise */
        r = (Math.random() * 16) | 0;
      }

      if (guidHolder[i] === "x") {
        guidResponse += hex[r];
      } else if (guidHolder[i] === "y") {
        // clock-seq-and-reserved first hex is filtered and remaining hex values are random
        /* tslint:disable-next-line:no-bitwise */
        r &= 0x3; // bit and with 0011 to set pos 2 to zero ?0??
        /* tslint:disable-next-line:no-bitwise */
        r |= 0x8; // set pos 3 to 1 as 1???
        guidResponse += hex[r];
      } else {
        guidResponse += guidHolder[i];
      }
    }
    return guidResponse;
  }
}
